'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { ArrowsPointingOutIcon, ArrowsPointingInIcon, XMarkIcon, ExclamationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { MessageBubble, ChatMessage, ChatInputArea, ChatInputAreaHandle } from '@contentgrowth/llm-service/ui/react';
import { useChat } from '@/hooks/useChat';
import { dispatchAssistantAction, AssistantAction, ASSISTANT_ACTION_RESULT_EVENT } from '@/lib/assistant-events';
import { apiClient } from '@/app/api/client';

interface AssistantHubProps {
  isOpen: boolean;
  onClose: () => void;
  onPromptHandled?: () => void; // Callback when prompt is answered/dismissed
  initialMode?: string;

}


export default function AssistantHub({ isOpen, onClose, onPromptHandled, initialMode }: AssistantHubProps) {
  const [isMaximized, setIsMaximized] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [closingMessage, setClosingMessage] = useState<string | null>(null);
  const chatInputRef = useRef<ChatInputAreaHandle>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const handleChatResponse = (response: any) => {
    console.log('[AssistantHub] handleChatResponse received:', response);

    if (response?.type === 'action') {
      const data = response?.data;
      console.log('[AssistantHub] Action detected, data:', data);

      if (data?.action) {
        const action: AssistantAction = {
          action: data.action.type,
          payload: data.action.payload,
          message: data.message
        };

        console.log('[AssistantHub] Created action object:', action);
        console.log('[AssistantHub] Action type:', action.action);

        // Handle close_chat action for onboarding completion
        if (action.action === 'close_chat') {
          console.log('[AssistantHub] ✅ close_chat detected! Starting countdown...');
          const countdown = action.payload?.countdown || 5;
          console.log('[AssistantHub] Countdown duration:', countdown);
          console.log('[AssistantHub] Message:', data.message);

          setClosingMessage(data.message || 'Chat will close soon...');
          setCountdownSeconds(countdown);

          console.log('[AssistantHub] State updated - countdown should start');

          // Don't dispatch this action, handle it locally
          return {
            id: `assistant-${Date.now()}`,
            content: data.message || 'Completing onboarding...',
            role: 'assistant' as const // Explicit role needed
          };
        }

        const messageId = `assistant-${Date.now()}`;
        const actionWithMessageId = { ...action, messageId };

        dispatchAssistantAction(actionWithMessageId);

        return {
          id: messageId,
          content: data.message || 'Processing action...'
        };
      }
    }
    return response?.data || 'Sorry, the request cannot be processed correctly, please try again!';
  };

  // Auto-maximize on mobile
  useEffect(() => {
    if (isOpen) {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setIsMaximized(true);
      }
    }
  }, [isOpen]);

  // --- Visual Viewport Handling for Mobile Keyboard ---
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    };

    if (window.visualViewport) {
      setViewportHeight(window.visualViewport.height);
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
    };
  }, []);

  // Memoize initialContext to prevent creating new object on every render
  const initialContext = useMemo(() =>
    initialMode ? { mode: initialMode } : undefined,
    [initialMode]
  );

  const {
    messages,
    setMessages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    error,
    retryInit,
    stop
  } = useChat({
    apiEndpoint: '/api/assistant/chat',
    historyEndpoint: '/api/assistant/start', // Start/resume session with smart welcome
    enabled: isOpen, // Only load history when the dialog is actually open
    onResponse: handleChatResponse,
    inputRef: chatInputRef, // Pass the ref for refocusing after submit
    initialContext,
  });

  // Auto-focus input when dialog opens AND after welcome message loads
  useEffect(() => {
    if (isOpen && !isLoading) {
      // Use setTimeout to ensure DOM has updated after message rendering
      const timer = setTimeout(() => {
        chatInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isLoading]);

  // Listen for action results to augment messages
  useEffect(() => {
    const handleActionResult = (event: Event) => {
      const customEvent = event as CustomEvent<{ messageId: string; result: any }>;
      const { messageId, result } = customEvent.detail;

      console.log('Received action result for message:', messageId, result);

      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId
            ? { ...msg, actionResult: result }
            : msg
        )
      );
    };

    window.addEventListener(ASSISTANT_ACTION_RESULT_EVENT, handleActionResult);

    return () => {
      window.removeEventListener(ASSISTANT_ACTION_RESULT_EVENT, handleActionResult);
    };
  }, [setMessages]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Debug: Log countdown state changes
  useEffect(() => {
    console.log('[AssistantHub] countdownSeconds changed:', countdownSeconds);
    console.log('[AssistantHub] closingMessage:', closingMessage);
  }, [countdownSeconds, closingMessage]);

  // Countdown timer effect
  useEffect(() => {
    if (countdownSeconds === null || countdownSeconds <= 0) {
      if (countdownSeconds === 0) {
        // Close the chat when countdown reaches 0
        onClose();
        // Reset countdown state
        setCountdownSeconds(null);
        setClosingMessage(null);
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdownSeconds(prev => prev !== null ? prev - 1 : null);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdownSeconds, onClose]);

  if (!isOpen) {
    return null;
  }

  const dialogSize = isMaximized ? 'w-full h-full md:w-[90vw] md:h-[90vh]' : 'w-full max-w-2xl h-[70vh]';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      style={isMaximized ? {
        height: viewportHeight ? `${viewportHeight}px` : '100%',
        maxHeight: '-webkit-fill-available',
        bottom: 'auto' // override inset-0 bottom
      } : undefined}
    >
      <div className={`bg-white md:rounded-lg shadow-xl flex flex-col transition-all duration-300 overflow-hidden ${dialogSize} ${isMaximized ? 'rounded-none h-full' : 'rounded-lg'}`}>
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center">
            <h2 className="text-xl font-bold">Content Strategist</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={() => setIsMaximized(!isMaximized)} className="text-gray-500 hover:text-gray-800">
              {isMaximized ? <ArrowsPointingInIcon className="h-6 w-6" /> : <ArrowsPointingOutIcon className="h-6 w-6" />}
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div ref={chatContainerRef} className="flex-grow p-4 overflow-y-auto overflow-x-hidden space-y-4">
          {/* Init Error State with Retry */}
          {error?.isInitError && !isLoading && messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <ExclamationCircleIcon className="h-12 w-12 text-red-500 mb-4" />
              <p className="text-red-600 text-center mb-4">{error.message}</p>
              {error.retryable && (
                <button
                  onClick={retryInit}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  Retry Connect
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Chat Messages */}
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} isUser={msg.role === 'user'} />
              ))}
              {isLoading && (
                <MessageBubble
                  message={{
                    id: 'loading',
                    role: 'assistant',
                    content: messages.length === 0 ? 'Connecting, please wait...' : 'Thinking...'
                  }}
                  isUser={false}
                />
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t">
          {/* Countdown banner for close_chat action - FIXED POSITION */}
          {countdownSeconds !== null && countdownSeconds > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 animate-pulse">
              <p className="text-green-800 font-medium">{closingMessage}</p>
              <p className="text-sm text-green-600 mt-2">
                Closing in {countdownSeconds} second{countdownSeconds !== 1 ? 's' : ''}...
              </p>
            </div>
          )}

          {error && !error.isInitError && (
            <div className="mb-2 flex items-center text-sm text-red-600">
              <ExclamationCircleIcon className="h-5 w-5 mr-2" />
              <span>{error.message}</span>
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
            <ChatInputArea
              ref={chatInputRef}
              value={input}
              onChange={handleInputChange}
              onSubmit={(msg) => handleSubmit({ preventDefault: () => { } } as React.FormEvent)}
              isSending={isLoading}
              onStop={stop}
              showInputForm={!error?.isInitError}
              currentTask={null}
              placeholder="Type a message..."
            />
          </form>
        </div>
      </div>
    </div>
  );
}
